import React from "react";

// Decorative three-quarter view of a modern mid-rise, drawn inline so the header
// needs no image asset. Sits behind the headline and is masked out on its left
// edge by .rd-hero-skyline, so contrast on the title is never affected.

const FLOORS = 6;
const FLOOR_H = 30;
const FACE_X = 250;
const FACE_W = 182;
const ROOF_Y = 44;
const GROUND_Y = 232;

// Warm interior lights, scattered rather than uniform so it reads as lived-in.
const LIT = new Set(["0-1", "1-0", "2-2", "3-1", "4-0", "4-2", "5-1"]);

const Floor = ({ index }) => {
  const top = ROOF_Y + index * FLOOR_H;
  const bays = [0, 1, 2];
  return (
    <g>
      {bays.map((bay) => {
        const x = FACE_X + 14 + bay * 55;
        const lit = LIT.has(`${index}-${bay}`);
        return (
          <rect
            key={`w-${index}-${bay}`}
            x={x}
            y={top + 3}
            width={44}
            height={17}
            rx={1.5}
            fill={lit ? "url(#rdGlassLit)" : "url(#rdGlass)"}
          />
        );
      })}

      {/* Projecting balcony slab + railing */}
      <rect x={FACE_X - 4} y={top + 22} width={FACE_W + 8} height={5} fill="url(#rdSlab)" />
      <rect x={FACE_X - 4} y={top + 14} width={FACE_W + 8} height={1.2} fill="rgba(255,255,255,0.45)" />
      {Array.from({ length: 15 }).map((_, i) => (
        <rect
          key={`r-${index}-${i}`}
          x={FACE_X - 2 + i * 12.5}
          y={top + 14}
          width={1}
          height={8}
          fill="rgba(255,255,255,0.28)"
        />
      ))}
    </g>
  );
};

const Tree = ({ x, y, scale = 1, tone = "#1f5750" }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <rect x={-1.6} y={-8} width={3.2} height={12} rx={1} fill="#123c38" />
    <ellipse cx={0} cy={-18} rx={11} ry={13} fill={tone} />
    <ellipse cx={-6} cy={-11} rx={8} ry={8} fill={tone} opacity="0.85" />
    <ellipse cx={6} cy={-12} rx={7.5} ry={8} fill={tone} opacity="0.7" />
  </g>
);

const HeroBuilding = () => (
  <svg
    className="rd-hero-skyline"
    viewBox="0 0 640 260"
    preserveAspectRatio="xMaxYMax slice"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="rdFaceLight" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#eaf2fd" />
        <stop offset="100%" stopColor="#aec2dc" />
      </linearGradient>
      <linearGradient id="rdFaceDark" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#8098bb" />
        <stop offset="100%" stopColor="#54678a" />
      </linearGradient>
      <linearGradient id="rdSlab" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f6faff" />
        <stop offset="100%" stopColor="#c3d3e8" />
      </linearGradient>
      <linearGradient id="rdGlass" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#2f5288" />
        <stop offset="100%" stopColor="#16294a" />
      </linearGradient>
      <linearGradient id="rdGlassLit" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ffe0ad" />
        <stop offset="100%" stopColor="#f0b869" />
      </linearGradient>
      <linearGradient id="rdCore" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#c7d6ea" />
        <stop offset="100%" stopColor="#93a9c8" />
      </linearGradient>
      <radialGradient id="rdHalo" cx="0.62" cy="0.42" r="0.55">
        <stop offset="0%" stopColor="#5b86c9" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#5b86c9" stopOpacity="0" />
      </radialGradient>
    </defs>

    <rect x="0" y="0" width="640" height="260" fill="url(#rdHalo)" />

    {/* Rear block, sets depth behind the main mass */}
    <rect x="180" y="96" width="72" height={GROUND_Y - 96} fill="url(#rdFaceDark)" opacity="0.75" />
    {Array.from({ length: 4 }).map((_, i) => (
      <rect key={`rb-${i}`} x="192" y={112 + i * 30} width="48" height="14" rx="1.5" fill="url(#rdGlass)" opacity="0.8" />
    ))}

    {/* Receding right face */}
    <polygon
      points={`${FACE_X + FACE_W},${ROOF_Y - 6} 498,${ROOF_Y + 16} 498,${GROUND_Y + 6} ${FACE_X + FACE_W},${GROUND_Y}`}
      fill="url(#rdFaceDark)"
    />
    {Array.from({ length: FLOORS }).map((_, i) => (
      <polygon
        key={`sf-${i}`}
        points={`${FACE_X + FACE_W + 8},${ROOF_Y + 4 + i * FLOOR_H} 490,${ROOF_Y + 24 + i * FLOOR_H} 490,${
          ROOF_Y + 40 + i * FLOOR_H
        } ${FACE_X + FACE_W + 8},${ROOF_Y + 22 + i * FLOOR_H}`}
        fill="url(#rdGlass)"
        opacity="0.85"
      />
    ))}

    {/* Main front face */}
    <rect x={FACE_X} y={ROOF_Y - 6} width={FACE_W} height={GROUND_Y - ROOF_Y + 6} fill="url(#rdFaceLight)" />
    <rect x={FACE_X - 6} y={ROOF_Y - 12} width={FACE_W + 12} height={7} rx={1.5} fill="url(#rdSlab)" />

    {Array.from({ length: FLOORS }).map((_, i) => (
      <Floor key={`f-${i}`} index={i} />
    ))}

    {/* Glazed circulation core */}
    <rect x={FACE_X + 78} y={ROOF_Y - 6} width={26} height={GROUND_Y - ROOF_Y + 6} fill="url(#rdCore)" opacity="0.95" />
    {Array.from({ length: 11 }).map((_, i) => (
      <rect key={`c-${i}`} x={FACE_X + 82} y={ROOF_Y + 2 + i * 17} width={18} height={11} rx={1} fill="url(#rdGlass)" opacity="0.7" />
    ))}

    {/* Ground floor: taller glazing and an entrance canopy */}
    <rect x={FACE_X} y={GROUND_Y - 26} width={FACE_W} height={26} fill="#1b3055" opacity="0.85" />
    {Array.from({ length: 5 }).map((_, i) => (
      <rect key={`g-${i}`} x={FACE_X + 8 + i * 34} y={GROUND_Y - 22} width={26} height={22} rx={1.5} fill="url(#rdGlass)" />
    ))}
    <rect x={FACE_X + 60} y={GROUND_Y - 30} width={70} height={4} rx={1.5} fill="url(#rdSlab)" />

    {/* Low podium wing */}
    <rect x="498" y="150" width="112" height={GROUND_Y - 150 + 6} fill="url(#rdFaceLight)" opacity="0.9" />
    {Array.from({ length: 3 }).map((_, i) => (
      <rect key={`p-${i}`} x="508" y={162 + i * 26} width="92" height="5" rx="1" fill="url(#rdSlab)" />
    ))}
    {Array.from({ length: 3 }).map((_, i) => (
      <rect key={`pw-${i}`} x="512" y={170 + i * 26} width="84" height="12" rx="1.5" fill="url(#rdGlass)" opacity="0.75" />
    ))}

    {/* Grade line and landscaping */}
    <rect x="120" y={GROUND_Y} width="520" height="4" fill="#0d2244" opacity="0.55" />
    <ellipse cx="380" cy={GROUND_Y + 8} rx="240" ry="9" fill="#08182f" opacity="0.4" />
    <Tree x={222} y={GROUND_Y + 2} scale={1.05} />
    <Tree x={470} y={GROUND_Y + 3} scale={1.2} tone="#22605a" />
    <Tree x={520} y={GROUND_Y + 2} scale={0.9} tone="#1b524c" />
    <Tree x={596} y={GROUND_Y + 3} scale={1.1} tone="#22605a" />
  </svg>
);

export { HeroBuilding };
